import { Injectable, inject, signal, WritableSignal } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { map, first, catchError } from 'rxjs/operators';
import {ApiCallService} from "./api-call.service";
import {Session} from "./session.service";
import { AccountStatus } from '../interfaces/account-status';

export interface SubscriptionPlan {
  requestKey: string;
  name: string;
  price: number;
  isTrial: boolean;
  pricePerSeat: number;
}

@Injectable({ providedIn: 'root' })
export class SubscriptionService {
  private api = inject(ApiCallService);
  private session = inject(Session);

  private accountStatusSignal: WritableSignal<AccountStatus | null> = signal<AccountStatus | null>(null);
  public get accountStatus() {
    return this.accountStatusSignal;
  }
  public setAccountStatus(status: AccountStatus) {
    this.accountStatusSignal.set(status);
  }

  public initAccountStatus() {
    this.setAccountStatus(
      this.session.getAccountStatus()
    )
  }

  getPlanDetailsAndPricing(): Observable<{ plan: SubscriptionPlan | null, seatCount: number, members: any[], error?: string }> {
    return forkJoin({
      plansResp: this.api.postJsonData('/public/getSubscriptionList', {}),
      seatsResp: this.api.postJsonData('/getAccountStatus', {}),
    }).pipe(
      first(),
      map(({ plansResp, seatsResp }) => {
        let plan = null;
        let seatCount = 0;
        let members: any[] = [];
        let error: string | undefined = undefined;
        if (!plansResp || plansResp.success === false) {
          error = Array.isArray(plansResp?.dataError) && plansResp.dataError.length > 0
            ? plansResp.dataError[0].message || plansResp.dataError[0].description
            : 'Failed to load subscription list.';
        } else {
          const plans = plansResp.data[0].dataSet.map((plan: any) => ({
            requestKey: plan.requestKey,
            name: plan.name,
            price: plan.amount,
            isTrial: plan.isTrial,
            pricePerSeat: plan.accountAdditionalUserCharge || 0,
          }));
          plan = plans.find((p: any) => p.isTrial === false) ?? null;
        }
        if (!seatsResp || seatsResp.success === false) {
          error = Array.isArray(seatsResp?.dataError) && seatsResp.dataError.length > 0
            ? seatsResp.dataError[0].message || seatsResp.dataError[0].description
            : 'Failed to load account status.';
        } else {
          let accountUsers = Array.isArray(seatsResp.data[0].dataSet.accountUsers) ? seatsResp.data[0].dataSet.accountUsers.filter(u => u.email !== this.session.getUser()?.userData.email) : [];
          seatCount = accountUsers.length;
          members = accountUsers;
        }
        return { plan, seatCount, members, error };
      }),
      catchError((err) => of({ plan: null, seatCount: 0, members: [], error: err?.message || 'Unknown error occurred.' }))
    );
  }

  createPaymentIntent(planRequestKey: string, emailsProjects?: { emails: string[], projects: string[]}): Observable<{ paymentIntentClientSecret: string | null, customerSessionClientSecret: string | null, error?: string }> {
    if (!planRequestKey) return of({ paymentIntentClientSecret: null, customerSessionClientSecret: null, error: 'No plan request key provided.' });
    const postObj = { subscriptionRequestKey: planRequestKey };
    if (emailsProjects) {
      const emails = emailsProjects.emails;
      const projects = emailsProjects.projects;
      postObj['newMembers'] = emails.map(email => ({ email, projects }));
    }
    return this.api.postJsonData('/public/getPaymentIntent', postObj)
      .pipe(
        first(),
        map(response => {
          if (response && response.success) {
            return {
              paymentIntentClientSecret: response.data[0].dataSet.paymentIntentClientSecret,
              customerSessionClientSecret: response.data[0].dataSet.customerSessionClientSecret,
            };
          } else {
            const errorMsg = Array.isArray(response?.dataError) && response.dataError.length > 0
              ? response.dataError[0].message || response.dataError[0].description
              : 'Failed to create payment intent.';
            return { paymentIntentClientSecret: null, customerSessionClientSecret: null, error: errorMsg };
          }
        }),
        catchError((err) => of({ paymentIntentClientSecret: null, customerSessionClientSecret: null, error: err?.message || 'Unknown error occurred.' }))
      );
  }
}
